/**
 * Small dense ridge-regression solver for gaze calibration.
 *
 * The calibration problem is tiny (a few hundred samples × ~20 features), so a
 * closed-form normal-equations solve with Gaussian elimination is both exact
 * and instant — no need for an ML runtime.
 */

/** Solve A·w = b for symmetric positive-definite A via Gaussian elimination with partial pivoting. */
function solve(a: number[][], b: number[]): number[] | null {
  const n = b.length
  // Augmented copies so callers' arrays are untouched.
  const m = a.map((row, i) => [...row, b[i]])
  for (let col = 0; col < n; col++) {
    let pivot = col
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(m[r][col]) > Math.abs(m[pivot][col])) pivot = r
    }
    if (Math.abs(m[pivot][col]) < 1e-12) return null // singular
    if (pivot !== col) {
      const tmp = m[col]
      m[col] = m[pivot]
      m[pivot] = tmp
    }
    for (let r = col + 1; r < n; r++) {
      const factor = m[r][col] / m[col][col]
      if (factor === 0) continue
      for (let c = col; c <= n; c++) m[r][c] -= factor * m[col][c]
    }
  }
  const w = new Array<number>(n).fill(0)
  for (let r = n - 1; r >= 0; r--) {
    let acc = m[r][n]
    for (let c = r + 1; c < n; c++) acc -= m[r][c] * w[c]
    w[r] = acc / m[r][r]
  }
  return w
}

/**
 * Fit ridge regression: minimise ‖X·w − y‖² + λ‖w‖².
 * The first column of X is assumed to be the bias term and is not regularised.
 * Returns the weight vector, or null if the system is singular.
 */
export function ridgeFit(x: number[][], y: number[], lambda: number): number[] | null {
  if (x.length === 0 || x.length !== y.length) return null
  const d = x[0].length
  // A = XᵀX + λI (bias unregularised), b = Xᵀy
  const a: number[][] = Array.from({ length: d }, () => new Array<number>(d).fill(0))
  const b = new Array<number>(d).fill(0)
  for (let s = 0; s < x.length; s++) {
    const row = x[s]
    for (let i = 0; i < d; i++) {
      b[i] += row[i] * y[s]
      for (let j = i; j < d; j++) a[i][j] += row[i] * row[j]
    }
  }
  for (let i = 0; i < d; i++) {
    for (let j = 0; j < i; j++) a[i][j] = a[j][i]
    if (i > 0) a[i][i] += lambda
  }
  return solve(a, b)
}

export function dot(w: number[], f: number[]): number {
  let acc = 0
  for (let i = 0; i < w.length; i++) acc += w[i] * f[i]
  return acc
}
